import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8081/api/v1';

export type AvailabilityType = 'VACATION' | 'SICK_LEAVE' | 'PERSONAL_LEAVE' | 'CUSTOM_HOURS' | 'BLOCKED';

export interface EmployeeAvailabilityOverride {
    id: string;
    employeeId: string;
    type: AvailabilityType;
    startDate: string; // ISO date
    endDate: string;   // ISO date
    startTime?: string; // HH:mm format
    endTime?: string;   // HH:mm format
    notes?: string;
    createdAt: string;
}

export interface AvailabilityCreateRequest {
    employeeId: string;
    type: AvailabilityType;
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
    notes?: string;
}

export interface AvailabilityUpdateRequest {
    type?: AvailabilityType;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    notes?: string;
}

export const employeeAvailabilityApi = {
    /**
     * Get all availability overrides for an employee
     */
    getEmployeeAvailability: async (employeeId: string): Promise<EmployeeAvailabilityOverride[]> => {
        const response = await axios.get(`${API_URL}/employees/${employeeId}/availability`);
        return response.data;
    },

    /**
     * Get availability overrides for a date range
     */
    getAvailabilityByDateRange: async (
        employeeId: string,
        startDate: string,
        endDate: string
    ): Promise<EmployeeAvailabilityOverride[]> => {
        const response = await axios.get(`${API_URL}/employees/${employeeId}/availability`, {
            params: { startDate, endDate }
        });
        return response.data;
    },

    /**
     * Create a new availability override
     */
    createAvailabilityOverride: async (request: AvailabilityCreateRequest): Promise<EmployeeAvailabilityOverride> => {
        const response = await axios.post(
            `${API_URL}/employees/${request.employeeId}/availability`,
            request
        );
        return response.data;
    },

    /**
     * Update an availability override
     */
    updateAvailabilityOverride: async (
        employeeId: string,
        overrideId: string,
        request: AvailabilityUpdateRequest
    ): Promise<EmployeeAvailabilityOverride> => {
        const response = await axios.put(
            `${API_URL}/employees/${employeeId}/availability/${overrideId}`,
            request
        );
        return response.data;
    },

    /**
     * Delete an availability override
     */
    deleteAvailabilityOverride: async (employeeId: string, overrideId: string): Promise<void> => {
        await axios.delete(`${API_URL}/employees/${employeeId}/availability/${overrideId}`);
    },

    /**
     * Check if an employee is available at a specific date/time
     */
    checkAvailability: async (
        employeeId: string,
        date: string,
        time: string
    ): Promise<{ available: boolean; reason?: string }> => {
        const response = await axios.get(`${API_URL}/employees/${employeeId}/availability/check`, {
            params: { date, time }
        });
        return response.data;
    },

    /**
     * Get all availability overrides for a salon (all employees)
     */
    getSalonAvailability: async (
        salonId: string,
        startDate: string,
        endDate: string
    ): Promise<EmployeeAvailabilityOverride[]> => {
        const response = await axios.get(`${API_URL}/salons/${salonId}/availability`, {
            params: { startDate, endDate }
        });
        return response.data;
    }
};

export default employeeAvailabilityApi;
